import { ExperimentStatus, ExperimentType } from "@prismancy/shared";
import { lazy, useEffect, useState } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useParams,
} from "react-router-dom";

import useExperiment from "./api/experiments/useExperiment";
import useVariants from "./api/variants/useVariants";
import AppWrapper from "./AppWrapper";
import EmailVerification from "./components/auth/EmailVerification";
import ForgotPassword from "./components/auth/ForgotPassword";
import ForgotPasswordVerification from "./components/auth/ForgotPasswordVerification";
import LoginEntry from "./components/auth/LoginEntry";
import SecretLogin from "./components/auth/SecretLogin";
import AuthHandoffReceiver from "./components/auth/AuthHandoffReceiver";
import useUserProfile from "./components/hooks/profile/useUserProfile";
import { Dashboard } from "./components/pages/Dashboard";
import {
  isRedirectTestWalkthroughComplete,
  resolveRedirectSetupRouteDecision,
} from "./components/pages/experiment/redirect/redirectTestUtils";
import PublishedExperienceChangeGuard from "./components/pages/experiment/setup/changes/PublishedExperienceChangeGuard";
import NotFound from "./components/pages/NotFound";
import RouteErrorBoundary from "./components/pages/RouteErrorBoundary";
import JoinWorkspace from "./components/workspace/JoinWorkspace";
import NoWorkspace from "./components/workspace/NoWorkspace";
import PendingInvites from "./components/workspace/PendingInvites";
import { LIST_EXPERIMENTS_LAST_STATUS } from "./constants";
import { LivePreviewProvider } from "./contexts/LivePreviewContext";
import AnonymousLayout from "./layouts/AnonymousLayout";
import AuthenticatedLayout from "./layouts/AuthenticatedLayout";
import VerificationLayout from "./layouts/VerificationLayout";
import WorkspaceLayout from "./layouts/WorkspaceLayout";
import {
  appRoutePatterns,
  appRoutes,
  EXPERIMENT_CHANGE_TYPES,
  resolveExperimentStatus,
} from "./routes/appRoutes";
import PrivateRoute from "./routes/PrivateRoute";
import PrivateWorkspaceRoute from "./routes/PrivateWorkspaceRoute";
import PublicRoute from "./routes/PublicRoute";
import WorkspaceAccessGuard from "./routes/WorkspaceAccessGuard";

const ActivityLog = lazy(() => import("./components/pages/ActivityLog"));
const AttributeChange = lazy(
  () => import("./components/pages/experiment/setup/changes/AttributeChange"),
);
const Billing = lazy(() => import("./components/pages/Billing"));
const CreateWorkspace = lazy(
  () => import("./components/workspace/CreateWorkspace"),
);
const ExperimentReporting = lazy(() =>
  import("./components/pages/ExperimentReporting").then((module) => ({
    default: module.ExperimentReporting,
  })),
);
const ExperimentSetup = lazy(() =>
  import("./components/pages/ExperimentSetup").then((module) => ({
    default: module.ExperimentSetup,
  })),
);
const ExperimentTargeting = lazy(() =>
  import("./components/pages/ExperimentTargeting").then((module) => ({
    default: module.ExperimentTargeting,
  })),
);
const HideChange = lazy(
  () => import("./components/pages/experiment/setup/changes/HideChange"),
);
const HtmlChange = lazy(
  () => import("./components/pages/experiment/setup/changes/HtmlChange"),
);
const LinkChange = lazy(
  () => import("./components/pages/experiment/setup/changes/LinkChange"),
);
const MoveChange = lazy(
  () => import("./components/pages/experiment/setup/changes/MoveChange"),
);
const PaymentFailed = lazy(() => import("./components/pages/PaymentFailed"));
const PaymentSuccessful = lazy(
  () => import("./components/pages/PaymentSuccessful"),
);
const RedirectTestOnboarding = lazy(
  () => import("./components/pages/RedirectTestOnboarding"),
);
const RedirectTestReviewPage = lazy(
  () => import("./components/pages/RedirectTestReviewPage"),
);
const RedirectTestSetup = lazy(
  () => import("./components/pages/RedirectTestSetup"),
);
const Settings = lazy(() => import("./components/pages/settings/Settings"));
const SettingsAnchorLinks = lazy(
  () => import("./components/pages/SettingsAnchorLinks"),
);
const StyleChange = lazy(
  () => import("./components/pages/experiment/setup/changes/StyleChange"),
);
const TemplateSettings = lazy(
  () => import("./components/pages/TemplateSettings"),
);
const TextChange = lazy(
  () => import("./components/pages/experiment/setup/changes/TextChange"),
);
const UIKitchenSink = lazy(() => import("./components/pages/UIKitchenSink"));
const VariationBuilder = lazy(
  () => import("./components/pages/experiment/setup/VariationBuilder"),
);
const ViewProfile = lazy(() => import("./components/pages/ViewProfile"));
const ViewTeam = lazy(() => import("./components/pages/ViewTeam"));
const WebhookInstructions = lazy(
  () => import("./components/pages/WebhookInstructions"),
);

const ExperimentLivePreviewBoundary = () => (
  <LivePreviewProvider>
    <Outlet />
  </LivePreviewProvider>
);

const ExperimentsIndexRoute = () => {
  const status = resolveExperimentStatus(
    localStorage.getItem(LIST_EXPERIMENTS_LAST_STATUS),
  );

  return <Navigate to={appRoutes.experiments.status(status)} replace />;
};

const ExperimentSetupRoute = () => {
  const { experimentId = "" } = useParams();
  const { experiment, isLoading: isLoadingExperiment } = useExperiment({
    experimentId,
  });
  const { variants, isLoading: isLoadingVariants } = useVariants({
    experimentId,
  });
  const [redirectSetupDecision, setRedirectSetupDecision] = useState<
    ReturnType<typeof resolveRedirectSetupRouteDecision> | null
  >(null);
  const isRedirectTest = experiment?.type === ExperimentType.REDIRECT;
  const walkthroughComplete = Boolean(
    isRedirectTest &&
    experiment &&
    isRedirectTestWalkthroughComplete(experiment, variants),
  );

  useEffect(() => {
    if (
      isLoadingExperiment ||
      isLoadingVariants ||
      !isRedirectTest ||
      !experiment
    )
      return;
    setRedirectSetupDecision((current) =>
      resolveRedirectSetupRouteDecision(experiment.id, current, walkthroughComplete),
    );
  }, [
    experiment,
    isLoadingExperiment,
    isLoadingVariants,
    isRedirectTest,
    walkthroughComplete,
  ]);

  if (isLoadingExperiment || (isRedirectTest && isLoadingVariants)) {
    return null;
  }

  if (isRedirectTest && experiment) {
    if (redirectSetupDecision?.experimentId !== experiment.id) return null;

    return redirectSetupDecision.showOnboarding ? (
      <RedirectTestOnboarding
        onComplete={() => setRedirectSetupDecision((current) =>
          current?.experimentId === experiment.id
            ? { ...current, showOnboarding: false }
            : current
        )}
      />
    ) : (
      <RedirectTestSetup />
    );
  }

  return <ExperimentSetup />;
};

type ExperimentTypeRouteProps = {
  type: ExperimentType;
  children: React.ReactElement;
};

const ExperimentTypeRoute = ({ type, children }: ExperimentTypeRouteProps) => {
  const { experimentId = "" } = useParams();
  const { experiment, isLoading } = useExperiment({ experimentId });

  if (isLoading) return null;
  if (experiment && experiment.type !== type) {
    return <Navigate to={appRoutes.experiments.setup(experiment.id)} replace />;
  }

  return children;
};

const CreateWorkspaceLayout = () => {
  const { profile, isLoading } = useUserProfile();

  if (isLoading) return null;
  return profile?.workspaces.length ? (
    <AuthenticatedLayout />
  ) : (
    <VerificationLayout />
  );
};

const changeElements = {
  attribute: <AttributeChange />,
  hide: <HideChange />,
  html: <HtmlChange />,
  link: <LinkChange />,
  move: <MoveChange />,
  style: <StyleChange />,
  text: <TextChange />,
} as const;

const experimentChangeRoutes = EXPERIMENT_CHANGE_TYPES.flatMap((changeType) => [
  {
    path: `:experimentId/:variantId/${changeType}`,
    element: (
      <ExperimentTypeRoute type={ExperimentType.AB}>
        <PublishedExperienceChangeGuard>
          {changeElements[changeType]}
        </PublishedExperienceChangeGuard>
      </ExperimentTypeRoute>
    ),
  },
  {
    path: `:experimentId/:variantId/${changeType}/:changeId`,
    element: (
      <ExperimentTypeRoute type={ExperimentType.AB}>
        <PublishedExperienceChangeGuard>
          {changeElements[changeType]}
        </PublishedExperienceChangeGuard>
      </ExperimentTypeRoute>
    ),
  },
]);

const router = createBrowserRouter(
  [
    {
      path: appRoutes.root,
      element: <AppWrapper />,
      errorElement: <RouteErrorBoundary />,
      children: [
        {
          index: true,
          element: <Navigate to={appRoutes.experiments.root} replace />,
        },
        {
          path: "index.html",
          element: <Navigate to={appRoutes.experiments.root} replace />,
        },
        {
          path: appRoutes.login,
          element: <PublicRoute restricted element={<AnonymousLayout />} />,
          children: [
            { index: true, element: <LoginEntry /> },
            { path: "secret", element: <SecretLogin /> },
            { path: "request-password-reset", element: <ForgotPassword /> },
            {
              path: "forgot-password-verification/:email",
              element: <ForgotPasswordVerification />,
            },
          ],
        },
        {
          path: "/auth/handoff",
          element: <AuthHandoffReceiver />,
        },
        {
          path: "/verification",
          element: <PublicRoute restricted element={<VerificationLayout />} />,
          children: [
            { path: "email-verification", element: <EmailVerification /> },
          ],
        },
        {
          path: "/authenticated-verification",
          element: <PrivateRoute element={<VerificationLayout />} />,
          children: [
            { path: "pending-invites/:email", element: <PendingInvites /> },
          ],
        },
        {
          path: "/workspace",
          element: <PrivateRoute element={<WorkspaceLayout />} />,
          children: [
            { path: "no-workspace-found", element: <NoWorkspace /> },
            { path: "join-workspace", element: <JoinWorkspace /> },
          ],
        },
        {
          path: appRoutes.experiments.root,
          element: <PrivateWorkspaceRoute element={<AuthenticatedLayout />} />,
          children: [
            { index: true, element: <ExperimentsIndexRoute /> },
            {
              path: ExperimentStatus.DRAFT,
              element: <Dashboard status={ExperimentStatus.DRAFT} />,
            },
            {
              path: ExperimentStatus.LIVE,
              element: <Dashboard status={ExperimentStatus.LIVE} />,
            },
            {
              path: ExperimentStatus.ENDED,
              element: <Dashboard status={ExperimentStatus.ENDED} />,
            },
            {
              element: <ExperimentLivePreviewBoundary />,
              children: [
                {
                  path: appRoutePatterns.experiments.setup,
                  element: <ExperimentSetupRoute />,
                },
                {
                  path: appRoutePatterns.experiments.targeting,
                  element: (
                    <ExperimentTypeRoute type={ExperimentType.AB}>
                      <ExperimentTargeting />
                    </ExperimentTypeRoute>
                  ),
                },
                {
                  path: appRoutePatterns.experiments.review,
                  element: (
                    <ExperimentTypeRoute type={ExperimentType.REDIRECT}>
                      <RedirectTestReviewPage />
                    </ExperimentTypeRoute>
                  ),
                },
                {
                  path: appRoutePatterns.experiments.reporting,
                  element: <ExperimentReporting />,
                },
                {
                  path: appRoutePatterns.experiments.variant,
                  element: (
                    <ExperimentTypeRoute type={ExperimentType.AB}>
                      <VariationBuilder />
                    </ExperimentTypeRoute>
                  ),
                },
                ...experimentChangeRoutes,
              ],
            },
          ],
        },
        {
          path: appRoutes.billing.root,
          element: <PrivateWorkspaceRoute element={<AuthenticatedLayout />} />,
          children: [
            {
              index: true,
              element: (
                <WorkspaceAccessGuard
                  allowed={(access) => access.canViewBilling}
                  title="You don't have permission to view billing"
                  message="Billing is managed by workspace admins. Ask an admin if you need access."
                >
                  <Billing />
                </WorkspaceAccessGuard>
              ),
            },
            {
              path: "success",
              element: (
                <WorkspaceAccessGuard
                  allowed={(access) => access.canViewBilling}
                  title="You don't have permission to view billing"
                  message="Billing is managed by workspace admins. Ask an admin if you need access."
                >
                  <PaymentSuccessful />
                </WorkspaceAccessGuard>
              ),
            },
            {
              path: "failed",
              element: (
                <WorkspaceAccessGuard
                  allowed={(access) => access.canViewBilling}
                  title="You don't have permission to view billing"
                  message="Billing is managed by workspace admins. Ask an admin if you need access."
                >
                  <PaymentFailed />
                </WorkspaceAccessGuard>
              ),
            },
          ],
        },
        {
          path: appRoutes.profile,
          element: <PrivateWorkspaceRoute element={<AuthenticatedLayout />} />,
          children: [{ index: true, element: <ViewProfile /> }],
        },
        {
          path: appRoutes.workspace.create,
          element: <PrivateRoute element={<CreateWorkspaceLayout />} />,
          children: [{ index: true, element: <CreateWorkspace /> }],
        },
        {
          path: appRoutes.settings.root,
          element: <PrivateRoute element={<AuthenticatedLayout />} />,
          children: [
            { path: "no-workspace-found", element: <NoWorkspace /> },
            { index: true, element: <Settings /> },
            { path: "install", element: <Settings /> },
            { path: "experiments", element: <Settings /> },
            { path: "integrations", element: <Settings /> },
            { path: "formatting", element: <Settings /> },
            { path: "domains", element: <Settings /> },
            { path: "team", element: <ViewTeam /> },
            { path: "design", element: <Settings /> },
            { path: "activity", element: <ActivityLog /> },
            { path: "anchor-links", element: <SettingsAnchorLinks /> },
          ],
        },
        { path: "/webhook-instructions", element: <WebhookInstructions /> },
        { path: "/ui-kitchen-sink", element: <UIKitchenSink /> },
        {
          path: "/template-settings",
          element: <PrivateRoute element={<AuthenticatedLayout />} />,
          children: [
            {
              index: true,
              element: (
                <LivePreviewProvider>
                  <TemplateSettings />
                </LivePreviewProvider>
              ),
            },
          ],
        },
      ],
    },
    { path: "*", element: <NotFound /> },
  ],
  {
    future: {
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_relativeSplatPath: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
);

export default router;
